import { useEffect, useRef, useState } from 'react';
import { Trophy, XCircle } from 'lucide-react';
import { Button, Field, Input, Select } from '@/ui/primitives';
import { useFechar, useRegistrarProposta } from '../../api';
import {
  PROPOSTA_CANAIS, PROPOSTA_PAGAMENTOS, PROPOSTA_PROBABILIDADES,
  RECUSA_MOTIVOS,
  type Detail,
} from '../../schemas';

interface Props {
  id: string;
  detail: Detail | undefined;
  clienteCpf: string | null | undefined;
}

function formatDate(s: string | null | undefined) {
  if (!s) return null;
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
}

// Fase Proposta — formulário de termos enviados + bloco de fechamento
// (Aprovado / Recusado). Pré-preenche com `detail.oportunidade.proposta_*`
// quando o cliente muda. Fechar exige motivo categórico ao recusar.
export function FaseProposta({ id, detail, clienteCpf }: Props) {
  const registrarProposta = useRegistrarProposta(id);
  const fechar = useFechar(id);

  const opp = detail?.oportunidade ?? {};

  // Form da proposta
  const [pPropCanal, setPPropCanal] = useState('');
  const [pPropValor, setPPropValor] = useState('');
  const [pPropValidade, setPPropValidade] = useState('');
  const [pPropPagamento, setPPropPagamento] = useState('');
  const [pPropEscopo, setPPropEscopo] = useState('');
  const [pPropLink, setPPropLink] = useState('');
  const [pPropProb, setPPropProb] = useState('');
  const [pPropObs, setPPropObs] = useState('');
  const propostaCpfRef = useRef<string | null>(null);

  // Form do fechamento
  const [motivoPerda, setMotivoPerda] = useState('');
  const [motivoPerdaCat, setMotivoPerdaCat] = useState('');
  const [valorGanho, setValorGanho] = useState('');

  // Pré-preenche proposta com dados salvos quando muda de cliente.
  useEffect(() => {
    const cpfAtual = clienteCpf ?? null;
    if (propostaCpfRef.current === cpfAtual) return;
    propostaCpfRef.current = cpfAtual;
    setPPropCanal(String(opp.proposta_canal ?? ''));
    setPPropValor(opp.proposta_valor != null ? String(opp.proposta_valor) : '');
    setPPropValidade(opp.proposta_validade ? String(opp.proposta_validade).slice(0, 10) : '');
    setPPropPagamento(String(opp.proposta_pagamento ?? ''));
    setPPropEscopo(String(opp.proposta_escopo ?? ''));
    setPPropLink(String(opp.proposta_link ?? ''));
    setPPropProb(String(opp.proposta_probabilidade ?? ''));
    setPPropObs('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteCpf, opp.proposta_canal, opp.proposta_valor, opp.proposta_validade,
      opp.proposta_pagamento, opp.proposta_escopo, opp.proposta_link, opp.proposta_probabilidade]);

  function onSubmitProposta() {
    registrarProposta.mutate({
      ...(pPropCanal && { canal: pPropCanal }),
      ...(pPropValor && { valor: Number(pPropValor) }),
      ...(pPropValidade && { validade: pPropValidade }),
      ...(pPropPagamento && { pagamento: pPropPagamento }),
      ...(pPropEscopo.trim() && { escopo: pPropEscopo.trim() }),
      ...(pPropLink.trim() && { link: pPropLink.trim() }),
      ...(pPropProb && { probabilidade: pPropProb }),
      ...(pPropObs.trim() && { observacao: pPropObs.trim() }),
    }, { onSuccess: () => setPPropObs('') });
  }

  function onFechar(resultado: 'ganho' | 'perdido') {
    if (resultado === 'perdido' && !motivoPerdaCat) return;
    fechar.mutate({
      resultado,
      ...(resultado === 'perdido' && {
        motivo_categoria: motivoPerdaCat,
        ...(motivoPerda.trim() && { motivo: motivoPerda.trim() }),
      }),
      ...(resultado === 'ganho' && valorGanho && { valor_fechado: Number(valorGanho) }),
    });
  }

  return (
    <section className="border border-border rounded-lg p-4 bg-elevated flex flex-col gap-4">
      <div>
        <h3 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-muted">
          Proposta enviada
        </h3>
        <p className="text-[0.78rem] text-text-2 mt-1">
          Registre os termos da proposta. Tudo fica salvo no card e vai pro histórico.
          {opp.proposta_enviada_em ? (
            <> Última atualização: {formatDate(String(opp.proposta_enviada_em))}.</>
          ) : null}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Canal de envio" htmlFor="crm-prop-canal">
          <Select id="crm-prop-canal" value={pPropCanal} onChange={(e) => setPPropCanal(e.target.value)} className="text-[0.82rem]">
            <option value="">—</option>
            {PROPOSTA_CANAIS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </Select>
        </Field>
        <Field label="Valor proposto (R$)" htmlFor="crm-prop-valor">
          <Input id="crm-prop-valor" type="number" min="0" step="100" value={pPropValor} onChange={(e) => setPPropValor(e.target.value)} placeholder="ex: 12000" />
        </Field>
        <Field label="Validade da proposta" htmlFor="crm-prop-val">
          <Input id="crm-prop-val" type="date" value={pPropValidade} onChange={(e) => setPPropValidade(e.target.value)} />
        </Field>
        <Field label="Forma de pagamento" htmlFor="crm-prop-pag">
          <Select id="crm-prop-pag" value={pPropPagamento} onChange={(e) => setPPropPagamento(e.target.value)} className="text-[0.82rem]">
            <option value="">—</option>
            {PROPOSTA_PAGAMENTOS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </Select>
        </Field>
      </div>

      <Field label="Escopo do serviço" htmlFor="crm-prop-esc">
        <textarea
          id="crm-prop-esc" value={pPropEscopo} onChange={(e) => setPPropEscopo(e.target.value)} rows={3}
          placeholder="O que está incluso. Ex: levantamento topográfico, retificação no SICAR, acompanhamento até liberação."
          className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text placeholder:text-text-muted hover:border-[var(--text-muted)] focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)] resize-none"
        />
      </Field>

      <Field label="Link da proposta (opcional)" htmlFor="crm-prop-link">
        <Input id="crm-prop-link" type="url" value={pPropLink} onChange={(e) => setPPropLink(e.target.value)} placeholder="https://… (drive, docs, anexo no whatsapp)" />
      </Field>

      <div className="flex flex-col gap-1.5">
        <span className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2">
          Probabilidade percebida de fechar
        </span>
        <div className="flex flex-wrap gap-2">
          {PROPOSTA_PROBABILIDADES.map((p) => {
            const active = pPropProb === p.v;
            const cls =
              p.tone === 'ok'
                ? active
                  ? 'bg-[rgba(16,185,129,0.18)] text-[var(--ok)] ring-2 ring-[var(--ok)]'
                  : 'bg-[rgba(16,185,129,0.10)] text-[var(--ok)] hover:bg-[rgba(16,185,129,0.18)]'
                : p.tone === 'warn'
                ? active
                  ? 'bg-[rgba(234,179,8,0.22)] text-[#a16207] dark:text-[#facc15] ring-2 ring-[#facc15]'
                  : 'bg-[rgba(234,179,8,0.14)] text-[#a16207] dark:text-[#facc15] hover:bg-[rgba(234,179,8,0.22)]'
                : active
                ? 'bg-[rgba(220,38,38,0.18)] text-[var(--danger)] ring-2 ring-[var(--danger)]'
                : 'bg-[rgba(220,38,38,0.10)] text-[var(--danger)] hover:bg-[rgba(220,38,38,0.18)]';
            return (
              <button key={p.v} type="button" onClick={() => setPPropProb(p.v)}
                className={'px-3 py-1 rounded-md text-[0.82rem] font-semibold border-0 transition-colors ' + cls}>
                {p.label}
              </button>
            );
          })}
        </div>
      </div>

      <Field label="Observação / contexto da negociação" htmlFor="crm-prop-obs">
        <textarea
          id="crm-prop-obs" value={pPropObs} onChange={(e) => setPPropObs(e.target.value)} rows={2}
          placeholder="Ex: cliente pediu desconto de 10%. Topa parcelar em 3 vezes. Quer falar com o filho antes."
          className="w-full rounded-md bg-elevated border-[1.5px] border-border2 px-3 py-2 text-sm text-text placeholder:text-text-muted hover:border-[var(--text-muted)] focus:border-accent focus:outline-none focus:shadow-[0_0_0_3px_rgba(233,106,69,0.15)] resize-none"
        />
      </Field>

      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="primary" size="sm" onClick={onSubmitProposta} disabled={registrarProposta.isPending}>
          {registrarProposta.isPending
            ? 'Salvando…'
            : opp.proposta_enviada_em
            ? 'Atualizar proposta'
            : 'Registrar proposta enviada'}
        </Button>
        <span className="text-[0.78rem] text-text-muted">Observação vira comentário no histórico.</span>
      </div>

      <div className="border-t border-border pt-4 mt-2 flex flex-col gap-4">
        <h4 className="text-[0.78rem] font-semibold uppercase tracking-wide text-text-2">Fechar resultado</h4>

        <div className="border border-border rounded-md p-3 bg-[rgba(16,185,129,0.04)] flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <Field label="Valor fechado (R$)" htmlFor="crm-valor-ganho">
              <Input id="crm-valor-ganho" type="number" value={valorGanho} onChange={(e) => setValorGanho(e.target.value)} placeholder="ex: 12000" />
            </Field>
          </div>
          <Button variant="primary" size="sm" onClick={() => onFechar('ganho')} disabled={fechar.isPending}>
            <Trophy size={14} /> Marcar como Aprovado
          </Button>
        </div>

        <div className="border border-border rounded-md p-3 bg-[rgba(220,38,38,0.04)] flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Motivo da recusa *" htmlFor="crm-motivo-cat">
              <Select id="crm-motivo-cat" value={motivoPerdaCat} onChange={(e) => setMotivoPerdaCat(e.target.value)} className="text-[0.82rem]">
                <option value="">Selecione um motivo</option>
                {RECUSA_MOTIVOS.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}
              </Select>
            </Field>
            <Field label="Detalhe (opcional)" htmlFor="crm-motivo-perda">
              <Input id="crm-motivo-perda" value={motivoPerda} onChange={(e) => setMotivoPerda(e.target.value)} placeholder="ex: pediu desconto de 30%" />
            </Field>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="danger" size="sm" onClick={() => onFechar('perdido')} disabled={fechar.isPending || !motivoPerdaCat}>
              <XCircle size={14} /> Marcar como Recusado
            </Button>
            <span className="text-[0.78rem] text-text-muted">
              {motivoPerdaCat ? 'Motivo será gravado na categoria do card e no histórico.' : '* selecione um motivo antes de recusar'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
